import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, X } from 'lucide-react';
import { ActivityTimeline } from '@/components/activity-timeline';
import { NodeList } from '@/components/node-list';
import { Badge, Button, Card, Empty, PageHeader } from '@/components/ui';
import { api } from '@/lib/api';
import { useNodes } from '@/lib/hooks';
import { fmtDateTime } from '@/lib/utils';

export function PermissionRequests() {
  const { data } = useQuery({
    queryKey: ['permissions', 'pending'],
    queryFn: () => api.permissions('pending'),
  });
  const decide = useMutation({
    mutationFn: (v: { id: number; d: 'approve' | 'reject' }) => api.decidePermission(v.id, v.d),
  });
  if (!data?.length) return <Empty>No pending permission requests.</Empty>;
  return (
    <ul className="space-y-2">
      {data.map((p) => (
        <li key={p.id} className="rounded-xl border border-warn/40 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <Badge tone="warn">{p.action}</Badge> <span className="font-medium">{p.requested_by_name}</span>{' '}
              <span className="text-xs text-muted-fg">{fmtDateTime(p.created_at)}</span>
            </div>
            <div className="flex gap-1">
              <Button size="sm" onClick={() => decide.mutate({ id: p.id, d: 'approve' })}>
                <Check size={13} /> Approve
              </Button>
              <Button size="sm" variant="outline" onClick={() => decide.mutate({ id: p.id, d: 'reject' })}>
                <X size={13} /> Reject
              </Button>
            </div>
          </div>
          {p.reason && <div className="mt-1 text-sm">{p.reason}</div>}
          <pre className="mt-1 overflow-x-auto rounded bg-muted p-2 text-xs">
            {JSON.stringify(p.payload, null, 2)}
          </pre>
        </li>
      ))}
    </ul>
  );
}

export function AgentsPage() {
  const { data: open, isLoading } = useNodes({ assignee_type: 'agent' });
  const { data: finished } = useNodes({ assignee_type: 'agent', status: ['done'] });
  const { data: sessions } = useQuery({ queryKey: ['agent-sessions'], queryFn: () => api.agentSessions(5) });
  const recent = (finished ?? [])
    .sort((a, b) => (b.completed_at ?? '').localeCompare(a.completed_at ?? ''))
    .slice(0, 10);
  return (
    <div className="space-y-4">
      <PageHeader title="Agents" subtitle="What agents work on, what they finished, and what they ask" />
      <Card className="p-4">
        <h2 className="mb-2 font-semibold">Pending permission requests</h2>
        <PermissionRequests />
      </Card>
      <Card className="p-4">
        <h2 className="mb-2 font-semibold">Assigned to agents</h2>
        <NodeList nodes={open} loading={isLoading} empty="No open agent tasks." />
      </Card>
      <Card className="p-4">
        <h2 className="mb-2 font-semibold">Recently finished by agents</h2>
        <NodeList nodes={recent} empty="Nothing finished yet." />
      </Card>
      {!!sessions?.length && (
        <Card className="p-4">
          <h2 className="mb-2 font-semibold">Recent agent sessions</h2>
          <ul className="space-y-2 text-sm">
            {sessions.map((s) => (
              <li key={s.id}>
                <span className="text-xs text-muted-fg">
                  {fmtDateTime(s.created_at)} · {s.user_name}
                </span>
                <div className="whitespace-pre-wrap">{s.summary_md}</div>
              </li>
            ))}
          </ul>
        </Card>
      )}
      <Card className="p-4">
        <h2 className="mb-2 font-semibold">Agent activity and questions</h2>
        <ActivityTimeline limit={40} showNode defaultKind="agent" />
      </Card>
    </div>
  );
}
